import { useId, useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Box, ChevronRight, Grid3x3, LayoutGrid, List, Package, QrCode } from 'lucide-react'
import {
  childTypesFor,
  finishOf,
  withFinish,
  type FurnitureFinish,
  detectLocationGrid,
  LOCATION_BATCH_CONFIRM_THRESHOLD,
  LOCATION_BATCH_MAX,
  planLocationBatchNames,
  type Entitlements,
  type Item,
  type Location,
  type LocationContents,
  type LocationType,
} from '@ousse/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useHousehold } from '../lib/household'
import { photoThumbUrl } from '../lib/photos'
import { readLocal, writeLocal } from '../lib/storage'
import { Alert, ConfirmButton, ErrorAlert, Field } from '../components/ui'
import { FinishPicker, FurnitureModelSelect, type CatalogPick } from '../components/FurnitureCatalog'

type ViewMode = 'list' | 'gallery' | 'cells'

function readViewMode(): ViewMode {
  const v = readLocal('ousse.viewMode')
  if (v === 'gallery' || v === 'cells' || v === 'list') return v
  return 'list'
}

/** Navigation dans l'arbre des rangements : liste, galerie ou cases (grille), création. */
export function StoragePage() {
  const { t } = useTranslation()
  const { me } = useAuth()
  const { current } = useHousehold()
  const [params] = useSearchParams()
  const locationId = params.get('locationId') || undefined
  const uncategorized = params.get('uncategorized') === '1'
  const hid = current?.id
  const canWrite = current && current.role !== 'viewer' && !current.readOnly
  const show3d = !(me?.user.simplifiedMode ?? true)
  const [error, setError] = useState<unknown>(null)
  const [view, setView] = useState<ViewMode>(readViewMode)
  /** false tant que l'utilisateur n'a pas choisi : on peut préférer Cases si grille. */
  const [userChoseView, setUserChoseView] = useState(() => readLocal('ousse.viewMode') != null)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const entitlements = useQuery({
    queryKey: ['entitlements', hid],
    enabled: Boolean(hid),
    queryFn: () => api.get<Entitlements>(`/households/${hid}/entitlements`),
  })
  /** Étiquettes en lot : fonction d'offre `bulkLabels`, masquée en mode simplifié (brief). */
  const bulkLabels = show3d && entitlements.data?.features.bulkLabels === true

  const setViewMode = (mode: ViewMode) => {
    setView(mode)
    writeLocal('ousse.viewMode', mode)
    setUserChoseView(true)
  }

  const contents = useQuery({
    queryKey: ['contents', hid, locationId ?? null],
    enabled: Boolean(hid) && !uncategorized,
    queryFn: () =>
      api.get<LocationContents>(
        `/households/${hid}/contents${locationId ? `?locationId=${encodeURIComponent(locationId)}` : ''}`,
      ),
  })

  const uncategorizedItems = useQuery({
    queryKey: ['items', hid, 'uncategorized'],
    enabled: Boolean(hid) && uncategorized,
    queryFn: () =>
      api.get<{ items: Item[] }>(`/households/${hid}/items?uncategorized=true&limit=100`),
  })

  const data = contents.data
  const gridLayout = useMemo(
    () => (data && !uncategorized ? detectLocationGrid(data.locations) : null),
    [data, uncategorized],
  )
  const hasGrid = Boolean(gridLayout)
  /**
   * Affichage des rangements enfants :
   * - grille détectée + pas encore de choix → cases (sans écraser `view`, pour que
   *   le premier clic « Liste » déclenche bien un re-render via `userChoseView`) ;
   * - sinon le mode choisi (`cells` retombe en liste s'il n'y a pas de grille).
   */
  const locView: ViewMode = !hasGrid
    ? view === 'cells'
      ? 'list'
      : view
    : userChoseView
      ? view
      : view === 'gallery'
        ? 'gallery'
        : 'cells'
  const itemView: ViewMode = locView === 'gallery' ? 'gallery' : 'list'

  if (!current) return <p role="status">{t('common.loading')}</p>
  if (uncategorized) {
    return (
      <>
        <nav className="breadcrumb" aria-label={t('inventory.storageTitle')}>
          <Link to="/storage">{t('inventory.breadcrumbRoot')}</Link>
          <ChevronRight aria-hidden size={18} />
          <span aria-current="page">{t('inventory.uncategorized')}</span>
        </nav>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h1>{t('inventory.uncategorized')}</h1>
          <ViewToggle view={view === 'cells' ? 'list' : view} onChange={setViewMode} showCells={false} />
        </div>
        <p className="muted">{t('inventory.uncategorizedHint')}</p>
        <ErrorAlert error={error} />
        <ItemBrowse
          items={uncategorizedItems.data?.items ?? []}
          householdId={current.id}
          canWrite={Boolean(canWrite)}
          view={view === 'gallery' ? 'gallery' : 'list'}
          onError={setError}
        />
      </>
    )
  }

  const empty = data && data.locations.length === 0 && data.items.length === 0

  return (
    <>
      <nav className="breadcrumb" aria-label={t('inventory.storageTitle')}>
        {locationId ? (
          <Link to="/storage">{t('inventory.breadcrumbRoot')}</Link>
        ) : (
          <span aria-current="page">{t('inventory.breadcrumbRoot')}</span>
        )}
        {data?.path.map((seg, i) => {
          const last = i === data.path.length - 1
          return (
            <span key={seg.id} className="row" style={{ gap: '0.25rem' }}>
              <ChevronRight aria-hidden size={18} />
              {last ? (
                <span aria-current="page">{seg.name}</span>
              ) : (
                <Link to={`/storage?locationId=${seg.id}`}>{seg.name}</Link>
              )}
            </span>
          )
        })}
      </nav>

      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h1>{data?.path.at(-1)?.name ?? t('inventory.storageTitle')}</h1>
        <div className="row">
          {show3d && (
            <Link
              className="btn secondary"
              to={locationId ? `/view3d?locationId=${encodeURIComponent(locationId)}` : '/view3d'}
            >
              <Box aria-hidden size={20} />
              {t('view3d.open')}
            </Link>
          )}
          {locationId && (
            <Link className="btn secondary" to={`/labels?ids=${encodeURIComponent(locationId)}`}>
              <QrCode aria-hidden size={20} />
              {t('labels.printOne')}
            </Link>
          )}
          {bulkLabels && hasGrid && data && data.locations.length > 0 && (
            <Link
              className="btn secondary"
              to={`/labels?ids=${encodeURIComponent(data.locations.map((l) => l.id).join(','))}`}
            >
              <QrCode aria-hidden size={20} />
              {t('labels.printAllCells')}
            </Link>
          )}
          <ViewToggle view={locView} onChange={setViewMode} showCells={hasGrid} />
        </div>
      </div>
      {!canWrite && <Alert kind="warning">{t('inventory.readOnlyHint')}</Alert>}
      <ErrorAlert error={error} />

      {!locationId && (data?.uncategorizedCount ?? 0) > 0 && (
        <p>
          <Link className="btn secondary" to="/storage?uncategorized=1">
            <Package aria-hidden size={20} />
            {t('inventory.uncategorizedCount', { count: data!.uncategorizedCount })}
          </Link>
        </p>
      )}

      {empty && <p className="muted">{locationId ? t('inventory.emptyItems') : t('inventory.emptyRoot')}</p>}

      {data && data.locations.length > 0 && (
        <>
          <h2>{t('inventory.locations')}</h2>
          {locView === 'cells' && gridLayout ? (
            <LocationCellsGrid
              locations={data.locations}
              layout={gridLayout}
              householdId={current.id}
            />
          ) : locView === 'gallery' ? (
            <ul className="gallery">
              {data.locations.map((loc) => (
                <li key={loc.id}>
                  <Link to={`/storage?locationId=${loc.id}`} className="gallery-card">
                    <Thumb householdId={current.id} photoId={loc.primaryPhotoId} alt={loc.name} />
                    <span>{loc.name}</span>
                    {typeof loc.itemCount === 'number' && (
                      <span className="muted">{t('inventory.cellItemCount', { count: loc.itemCount })}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="list">
              {data.locations.map((loc) => (
                <li key={loc.id}>
                  <Link
                    to={`/storage?locationId=${loc.id}`}
                    className="row"
                    style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
                  >
                    <Thumb householdId={current.id} photoId={loc.primaryPhotoId} alt={loc.name} small />
                    <span>
                      <strong>{loc.name}</strong>
                      <br />
                      <span className="muted">
                        {t(`inventory.type.${loc.type}`)}
                        {typeof loc.itemCount === 'number'
                          ? ` · ${t('inventory.cellItemCount', { count: loc.itemCount })}`
                          : ''}
                      </span>
                    </span>
                  </Link>
                  {canWrite && (
                    <ConfirmButton
                      message={t('inventory.deleteLocationConfirm', { name: loc.name })}
                      onConfirm={async () => {
                        setError(null)
                        try {
                          await api.delete(`/households/${current.id}/locations/${loc.id}`)
                          await qc.invalidateQueries({ queryKey: ['contents', hid] })
                        } catch (e) {
                          setError(e)
                        }
                      }}
                    >
                      {t('inventory.deleteLocation')}
                    </ConfirmButton>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {data && data.items.length > 0 && (
        <>
          <h2>{t('inventory.items')}</h2>
          <ItemBrowse
            items={data.items}
            householdId={current.id}
            canWrite={Boolean(canWrite)}
            view={itemView}
            onError={setError}
          />
        </>
      )}

      {canWrite && (
        <CreateLocationForm
          householdId={current.id}
          parentId={locationId ?? null}
          parentType={data?.path.at(-1)?.type ?? null}
          onCreated={(id) => {
            void qc.invalidateQueries({ queryKey: ['contents', hid] })
            navigate(`/storage?locationId=${id}`)
          }}
          onError={setError}
        />
      )}

      {canWrite && locationId && (
        <BatchCreateForm
          householdId={current.id}
          parentId={locationId}
          onCreated={(createdIds) => {
            void qc.invalidateQueries({ queryKey: ['contents', hid] })
            if (createdIds.length > 0 && bulkLabels) {
              navigate(`/labels?ids=${encodeURIComponent(createdIds.join(','))}`)
            }
          }}
          onError={setError}
        />
      )}

      {canWrite && (
        <p>
          <Link className="btn" to={locationId ? `/add?locationId=${locationId}` : '/add'}>
            {t('home.addItem')}
          </Link>
        </p>
      )}
    </>
  )
}

function ViewToggle({
  view,
  onChange,
  showCells,
}: {
  view: ViewMode
  onChange: (m: ViewMode) => void
  showCells: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="row" role="group" aria-label={t('inventory.viewMode')}>
      <button
        type="button"
        className={`btn ${view === 'list' ? '' : 'ghost'}`}
        aria-pressed={view === 'list'}
        onClick={() => onChange('list')}
      >
        <List aria-hidden size={20} />
        {t('inventory.viewList')}
      </button>
      {showCells && (
        <button
          type="button"
          className={`btn ${view === 'cells' ? '' : 'ghost'}`}
          aria-pressed={view === 'cells'}
          onClick={() => onChange('cells')}
        >
          <Grid3x3 aria-hidden size={20} />
          {t('inventory.viewCells')}
        </button>
      )}
      <button
        type="button"
        className={`btn ${view === 'gallery' ? '' : 'ghost'}`}
        aria-pressed={view === 'gallery'}
        onClick={() => onChange('gallery')}
      >
        <LayoutGrid aria-hidden size={20} />
        {t('inventory.viewGallery')}
      </button>
    </div>
  )
}

function LocationCellsGrid({
  locations,
  layout,
  householdId,
}: {
  locations: Location[]
  layout: NonNullable<ReturnType<typeof detectLocationGrid>>
  householdId: string
}) {
  const { t } = useTranslation()
  const byId = useMemo(() => new Map(locations.map((l) => [l.id, l])), [locations])

  return (
    <ul
      className="cells-grid"
      style={{ gridTemplateColumns: `repeat(${layout.cols}, minmax(4.5rem, 1fr))` }}
      aria-label={t('inventory.viewCells')}
    >
      {Array.from({ length: layout.rows }, (_, r) =>
        Array.from({ length: layout.cols }, (_, c) => {
          const ref = layout.byCoord.get(`${r},${c}`)
          const loc = ref ? byId.get(ref.id) : undefined
          if (!loc) {
            return <li key={`${r}-${c}`} className="cells-grid-empty" aria-hidden />
          }
          const count = loc.itemCount ?? 0
          return (
            <li key={loc.id}>
              <Link
                to={`/storage?locationId=${loc.id}`}
                className="cells-grid-cell"
                aria-label={`${loc.name}. ${t('inventory.cellItemCount', { count })}`}
              >
                <Thumb householdId={householdId} photoId={loc.primaryPhotoId} alt={loc.name} />
                <span className="cells-grid-name">{loc.name}</span>
                <span className="muted cells-grid-count">{t('inventory.cellItemCount', { count })}</span>
              </Link>
            </li>
          )
        }),
      )}
    </ul>
  )
}

function Thumb({
  householdId,
  photoId,
  alt,
  small,
}: {
  householdId: string
  photoId?: string | null
  alt: string
  small?: boolean
}) {
  const { t } = useTranslation()
  if (!photoId) {
    return (
      <span className={`thumb-placeholder ${small ? 'small' : ''}`} aria-hidden>
        <Package size={small ? 22 : 40} />
      </span>
    )
  }
  return (
    <img
      src={photoThumbUrl(householdId, photoId)}
      alt={alt || t('inventory.untitledItem')}
      className={`thumb ${small ? 'small' : ''}`}
      loading="lazy"
    />
  )
}

function ItemBrowse({
  items,
  householdId,
  canWrite,
  view,
  onError,
}: {
  items: Item[]
  householdId: string
  canWrite: boolean
  view: ViewMode
  onError: (e: unknown) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  if (items.length === 0) return <p className="muted">{t('inventory.emptyItems')}</p>

  if (view === 'gallery') {
    return (
      <ul className="gallery">
        {items.map((item) => {
          const label = item.name?.trim() || t('inventory.untitledItem')
          return (
            <li key={item.id}>
              <Link to={`/items/${item.id}`} className="gallery-card">
                <Thumb householdId={householdId} photoId={item.primaryPhotoId} alt={label} />
                <span>{label}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <ul className="list">
      {items.map((item) => {
        const label = item.name?.trim() || t('inventory.untitledItem')
        return (
          <li key={item.id}>
            <Link to={`/items/${item.id}`} className="row" style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}>
              <Thumb householdId={householdId} photoId={item.primaryPhotoId} alt={label} small />
              <span>
                <strong>{label}</strong>
                {item.quantity > 1 && (
                  <>
                    <br />
                    <span className="muted">{t('inventory.quantity', { count: item.quantity })}</span>
                  </>
                )}
              </span>
            </Link>
            {canWrite && (
              <ConfirmButton
                message={t('inventory.deleteItemConfirm', { name: label })}
                onConfirm={async () => {
                  onError(null)
                  try {
                    await api.delete(`/households/${householdId}/items/${item.id}`)
                    await qc.invalidateQueries()
                  } catch (e) {
                    onError(e)
                  }
                }}
              >
                {t('inventory.deleteItem')}
              </ConfirmButton>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function CreateLocationForm({
  householdId,
  parentId,
  parentType,
  onCreated,
  onError,
}: {
  householdId: string
  parentId: string | null
  parentType: LocationType | null
  onCreated: (id: string) => void
  onError: (e: unknown) => void
}) {
  const { t } = useTranslation()
  const kindId = useId()
  const [name, setName] = useState('')
  // Type proposé selon le parent (le plus probable d'abord), comme sur l'appli ; on peut en choisir un autre.
  const types = childTypesFor(parentType)
  const [picked, setPicked] = useState<LocationType | null>(null)
  const type = picked && types.includes(picked) ? picked : types[0]!
  // Meuble : modèle du catalogue ou de « mes modèles » (taille, dessin, intérieur créés avec lui).
  const [model, setModel] = useState('')
  const [modelPick, setModelPick] = useState<CatalogPick | null>(null)
  const [finish, setFinish] = useState<FurnitureFinish>('natural')
  const useModel = type === 'furniture' && parentId !== null && modelPick !== null
  const create = useMutation({
    mutationFn: async (): Promise<string> => {
      if (useModel) {
        const res = await api.post<{ furniture: Location }>(`/households/${householdId}/locations/furniture`, {
          name: name.trim(),
          parentId,
          spec: finish === 'natural' ? modelPick!.spec : withFinish(modelPick!.spec, finish),
        })
        return res.furniture.id
      }
      return (await api.post<Location>(`/households/${householdId}/locations`, { name: name.trim(), parentId, type })).id
    },
    onSuccess: (id) => {
      setName('')
      setPicked(null)
      setModel('')
      setModelPick(null)
      setFinish('natural')
      onCreated(id)
    },
    onError,
  })

  return (
    <form
      className="card stack"
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        onError(null)
        if (!name.trim()) return
        create.mutate()
      }}
    >
      <h2>{t('inventory.addLocation')}</h2>
      <Field
        label={t('inventory.locationName')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('inventory.locationNamePlaceholder')}
        required
        maxLength={120}
      />
      <fieldset className="radio-group" aria-describedby={`${kindId}-hint`}>
        <legend>{t('inventory.locationKind')}</legend>
        <div className="row">
          {types.map((option) => (
            <label key={option} className="checkbox">
              <input type="radio" name={kindId} value={option} checked={type === option} onChange={() => setPicked(option)} />
              {t(`inventory.type.${option}`)}
            </label>
          ))}
        </div>
        <span id={`${kindId}-hint`} className="muted">
          {t(`inventory.locationKindHint.${type}`)}
          {(type === 'place' || type === 'room') && (
            <>
              <br />
              {t('inventory.outbuildingTip')}
            </>
          )}
        </span>
      </fieldset>
      {type === 'furniture' && parentId !== null && (
        <FurnitureModelSelect
          householdId={householdId}
          value={model}
          onChange={(value, pick) => {
            // Le nom suit le modèle tant qu'on ne l'a pas changé à la main.
            if (!name.trim() || name === modelPick?.name) setName(pick?.name ?? '')
            setModel(value)
            setModelPick(pick)
            setFinish(pick ? finishOf(pick.spec) : 'natural')
          }}
        />
      )}
      {useModel && <FinishPicker value={finish} onChange={setFinish} />}
      <button type="submit" className="btn" disabled={create.isPending || !name.trim()}>
        {t('inventory.createLocation')}
      </button>
    </form>
  )
}

function BatchCreateForm({
  householdId,
  parentId,
  onCreated,
  onError,
}: {
  householdId: string
  parentId: string
  onCreated: (createdIds: string[]) => void
  onError: (e: unknown) => void
}) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'grid' | 'series'>('grid')
  const [rows, setRows] = useState(2)
  const [cols, setCols] = useState(3)
  const [count, setCount] = useState(4)
  const [prefix, setPrefix] = useState('')
  const [naming, setNaming] = useState<'number' | 'letter'>('number')

  const preview = useMemo(() => {
    try {
      if (mode === 'grid') {
        if (rows < 1 || cols < 1 || rows * cols > LOCATION_BATCH_MAX) return []
        return planLocationBatchNames({
          mode: 'grid',
          rows,
          cols,
          prefix: prefix.trim() || undefined,
        })
      }
      if (count < 1 || count > LOCATION_BATCH_MAX || !prefix.trim()) return []
      return planLocationBatchNames({ mode: 'series', prefix: prefix.trim(), count, naming })
    } catch {
      return []
    }
  }, [mode, rows, cols, count, prefix, naming])

  const create = useMutation({
    mutationFn: () => {
      if (mode === 'grid') {
        return api.post<{ items: Location[] }>(`/households/${householdId}/locations/batch`, {
          mode: 'grid',
          parentId,
          rows,
          cols,
          prefix: prefix.trim() || undefined,
        })
      }
      return api.post<{ items: Location[] }>(`/households/${householdId}/locations/batch`, {
        mode: 'series',
        parentId,
        prefix: prefix.trim(),
        count,
        naming,
      })
    },
    onSuccess: (res) => {
      setPrefix('')
      onCreated(res.items.map((i) => i.id))
    },
    onError,
  })

  const total = preview.length
  const previewNames = preview.slice(0, 12).map((p) => p.name)

  return (
    <form
      className="card stack"
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        onError(null)
        if (total < 1) return
        if (total > LOCATION_BATCH_CONFIRM_THRESHOLD) {
          const ok = window.confirm(t('inventory.batchConfirmLarge', { count: total }))
          if (!ok) return
        }
        create.mutate()
      }}
    >
      <h2>{t('inventory.batchTitle')}</h2>
      <p className="muted">{t('inventory.batchHint')}</p>
      <p className="muted">{t('labels.afterBatchHint')}</p>
      <div className="row" role="group" aria-label={t('inventory.batchMode')}>
        <button
          type="button"
          className={`btn ${mode === 'grid' ? '' : 'ghost'}`}
          aria-pressed={mode === 'grid'}
          onClick={() => setMode('grid')}
        >
          <LayoutGrid aria-hidden size={20} />
          {t('inventory.batchModeGrid')}
        </button>
        <button
          type="button"
          className={`btn ${mode === 'series' ? '' : 'ghost'}`}
          aria-pressed={mode === 'series'}
          onClick={() => setMode('series')}
        >
          <List aria-hidden size={20} />
          {t('inventory.batchModeSeries')}
        </button>
      </div>

      {mode === 'grid' ? (
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <Field
            label={t('inventory.batchRows')}
            type="number"
            min={1}
            max={50}
            value={String(rows)}
            onChange={(e) => setRows(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            required
          />
          <Field
            label={t('inventory.batchCols')}
            type="number"
            min={1}
            max={50}
            value={String(cols)}
            onChange={(e) => setCols(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            required
          />
        </div>
      ) : (
        <>
          <Field
            label={t('inventory.batchCount')}
            type="number"
            min={1}
            max={LOCATION_BATCH_MAX}
            value={String(count)}
            onChange={(e) => setCount(Math.max(1, Math.min(LOCATION_BATCH_MAX, Number(e.target.value) || 1)))}
            required
          />
          <label className="stack">
            <span>{t('inventory.batchNaming')}</span>
            <select
              value={naming}
              onChange={(e) => setNaming(e.target.value as 'number' | 'letter')}
              style={{ minHeight: 48 }}
            >
              <option value="number">{t('inventory.batchNamingNumber')}</option>
              <option value="letter">{t('inventory.batchNamingLetter')}</option>
            </select>
          </label>
        </>
      )}

      <Field
        label={t('inventory.batchPrefix')}
        value={prefix}
        onChange={(e) => setPrefix(e.target.value)}
        placeholder={
          mode === 'grid' ? t('inventory.batchPrefixPlaceholderGrid') : t('inventory.batchPrefixPlaceholderSeries')
        }
        required={mode === 'series'}
        maxLength={mode === 'series' ? 80 : 40}
      />

      {previewNames.length > 0 && (
        <div>
          <p>
            <strong>{t('inventory.batchPreview')}</strong>
          </p>
          <p className="muted">{previewNames.join(', ')}</p>
          {total > previewNames.length && (
            <p className="muted">{t('inventory.batchPreviewMore', { count: total - previewNames.length })}</p>
          )}
        </div>
      )}

      <button type="submit" className="btn" disabled={create.isPending || total < 1}>
        {t('inventory.batchCreate')}
      </button>
    </form>
  )
}
